/**
 * Owner: Masa Bayu
 * File: MarketplaceHome.tsx
 * Date:Time: 2026-10-22 23:59
 * Description: Senarai penuras/isih/muka surat + troli live — kini di /all-books
 *   & /bestsellers (home §376 ialah landing mockup). `search` (query string)
 *   menyokong penuras awal: category, subcategory, q, sort.
 *   Locale: default English + USD, auto-tukar ikut negara (ms-MY → BM + MYR).
 *   §466 S1 — `rating`/`bestseller` dibuang daripada URL: penilaiannya bukan data hub.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactElement } from 'react';
import './marketplace.css';
import { addDemoOfferToCart } from '../../commerce/demo-cart-store';
import { hasCatalogOffer } from '../../commerce/catalog-offers';
import { LandingIcon } from './landing/LandingIcons';
import { getCatalogVersion, subscribeCatalog } from '../../publication/catalog-port';
import { BannerCarousel } from './BannerCarousel';
import { FilterSidebar } from './FilterSidebar';
import { MarketplaceCategoryStrip } from './MarketplaceCategoryStrip';
import { BookCard } from './BookCard';
import { MarketplaceFooter } from './MarketplaceFooter';
import {
  getMarketplaceLocaleProfile,
  formatShowing,
  resolveMarketplaceLabels,
} from './marketplace-locale';
import {
  computeFacetCounts,
  listMarketplaceBooks,
  MARKETPLACE_CATEGORY_ORDER,
  MARKETPLACE_FILTERS_EMPTY,
  MARKETPLACE_SORT_ORDER,
  matchesFilters,
  paginateMarketplaceBooks,
  sortMarketplaceBooks,
  type MarketplaceBook,
  type MarketplaceFilters,
  type MarketplaceSortId,
} from './marketplace-projection';
import { isQxSubcategoryId, qxParentOfSubcategory } from '@qx/qxread-core';

export interface MarketplaceHomeProps {
  readonly onNavigate: (path: string) => void;
  readonly search?: string;
}

function initialFiltersFromSearch(search: string | undefined): MarketplaceFilters {
  if (typeof search !== 'string' || search.length === 0) {
    return MARKETPLACE_FILTERS_EMPTY;
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const category = params.get('category');
  const sub = params.get('subcategory');
  const validCategory =
    category !== null && (MARKETPLACE_CATEGORY_ORDER as readonly string[]).includes(category);
  // §405 — subkategori hanya sah jika induk padan; induk boleh disimpulkan dari sub.
  const validSub = sub !== null && isQxSubcategoryId(sub);
  const subParent = validSub ? qxParentOfSubcategory(sub) : undefined;
  const resolvedCategory = validCategory
    ? (category as MarketplaceFilters['category'])
    : subParent !== undefined
      ? (subParent as MarketplaceFilters['category'])
      : 'all';
  const resolvedSub = validSub && subParent === resolvedCategory ? sub : 'all';
  return {
    ...MARKETPLACE_FILTERS_EMPTY,
    category: resolvedCategory,
    subcategory: resolvedSub,
    query: params.get('q') ?? '',
  };
}

function initialSortFromSearch(search: string | undefined): MarketplaceSortId {
  if (typeof search !== 'string' || search.length === 0) {
    return 'newest';
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const sort = params.get('sort');
  return sort !== null && (MARKETPLACE_SORT_ORDER as readonly string[]).includes(sort)
    ? (sort as MarketplaceSortId)
    : 'newest';
}

export function MarketplaceHome({ onNavigate, search }: MarketplaceHomeProps): ReactElement {
  // Re-render apabila sumber catalog bertukar (overlay hub sync tiba selepas
  // render pertama) — books diprojek semula daripada catalog terkini.
  const catalogVersion = useSyncExternalStore(subscribeCatalog, getCatalogVersion);
  const books = useMemo(() => listMarketplaceBooks(), [catalogVersion]);
  const [filters, setFilters] = useState<MarketplaceFilters>(() =>
    initialFiltersFromSearch(search),
  );
  const [sortId, setSortId] = useState<MarketplaceSortId>(() => initialSortFromSearch(search));
  const [page, setPage] = useState(1);
  const [addedId, setAddedId] = useState<string | null>(null);
  const addedTimer = useRef<number | null>(null);

  const profile = useMemo(() => getMarketplaceLocaleProfile(), []);
  const t = useMemo(() => resolveMarketplaceLabels(profile), [profile]);

  useEffect(
    () => () => {
      if (addedTimer.current !== null) {
        window.clearTimeout(addedTimer.current);
      }
    },
    [],
  );

  const filtered = useMemo(
    () => books.filter((book) => matchesFilters(book, filters)),
    [books, filters],
  );
  const sorted = useMemo(() => sortMarketplaceBooks(filtered, sortId), [filtered, sortId]);
  const pageData = useMemo(() => paginateMarketplaceBooks(sorted, page), [sorted, page]);
  const counts = useMemo(() => computeFacetCounts(books, filters), [books, filters]);

  const applyFilters = (next: MarketplaceFilters): void => {
    setFilters(next);
    setPage(1);
  };

  const onBuy = (book: MarketplaceBook): void => {
    // §466 S2 — tanpa tawaran hub tiada CTA beli; penambahan troli tidak
    // mungkin gagal-keras kerana gerbang dan stor membaca sumber yang sama.
    if (!hasCatalogOffer(book.publicationId)) return;
    addDemoOfferToCart(book.publicationId);
    setAddedId(book.publicationId);
    if (addedTimer.current !== null) {
      window.clearTimeout(addedTimer.current);
    }
    addedTimer.current = window.setTimeout(() => {
      setAddedId(null);
    }, 1600);
  };

  return (
    <div className="hmk hmk--home">
      {/* §401 — MarketplaceNav dibuang: shell sudah memakai MarketplaceTopBar
          sebagai chrome tunggal; header dua baris bertindih di <768/≥768. */}
      <h1 className="visually-hidden">{t.hero.title}</h1>
      <div className="hmk__body">
        <FilterSidebar t={t} filters={filters} counts={counts} onChange={applyFilters} />
        <section className="hmk__results" aria-label={t.results.ariaLabel}>
          {/* §410 — hero + strip ikut kaedah home: dalam lajur tengah, bukan
              full-bleed atas halaman. */}
          <BannerCarousel t={t} />
          {/* §392 — <768: semua kategori ikut corak home (sidebar lama disembunyi). */}
          <MarketplaceCategoryStrip t={t} filters={filters} onChange={applyFilters} />
          <div className="hmk__results-head">
            <p className="hmk__showing">
              {formatShowing(
                pageData.showingFrom,
                pageData.showingTo,
                pageData.total,
                profile.language,
              )}
            </p>
            <label className="hmk__sort">
              {t.results.sortBy}
              <select
                value={sortId}
                onChange={(event) => {
                  setSortId(event.target.value as MarketplaceSortId);
                  setPage(1);
                }}
              >
                {MARKETPLACE_SORT_ORDER.map((id) => (
                  <option key={id} value={id}>
                    {t.sorts[id]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {pageData.total === 0 ? (
            <div className="hmk__empty">
              <p className="hmk__empty-title">{t.results.emptyTitle}</p>
              <p className="hmk__empty-sub">{t.results.emptySub}</p>
              <button
                type="button"
                className="hmk__buy-btn hmk__empty-reset"
                onClick={() => {
                  applyFilters(MARKETPLACE_FILTERS_EMPTY);
                }}
              >
                {t.results.resetFilters}
              </button>
            </div>
          ) : (
            <>
              <ul className="hmk__grid" aria-label={t.card.gridAriaLabel}>
                {pageData.items.map((book) => (
                  <BookCard
                    key={book.publicationId}
                    t={t}
                    book={book}
                    added={addedId === book.publicationId}
                    purchasable={hasCatalogOffer(book.publicationId)}
                    onNavigate={onNavigate}
                    onBuy={onBuy}
                  />
                ))}
              </ul>
              <nav className="hmk__pagination" aria-label={t.pagination.ariaLabel}>
                <button
                  type="button"
                  className="hmk__page-btn"
                  aria-label={t.pagination.previous}
                  disabled={pageData.page <= 1}
                  onClick={() => {
                    setPage(pageData.page - 1);
                  }}
                >
                  <LandingIcon name="chevronLeft" size={14} />
                </button>
                {Array.from({ length: pageData.pageCount }, (_, index) => index + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    className="hmk__page-btn"
                    data-active={n === pageData.page}
                    {...(n === pageData.page ? { 'aria-current': 'page' as const } : {})}
                    onClick={() => {
                      setPage(n);
                    }}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  className="hmk__page-btn"
                  aria-label={t.pagination.next}
                  disabled={pageData.page >= pageData.pageCount}
                  onClick={() => {
                    setPage(pageData.page + 1);
                  }}
                >
                  <LandingIcon name="chevronRight" size={14} />
                </button>
              </nav>
            </>
          )}
        </section>
      </div>
      <MarketplaceFooter t={t} />
    </div>
  );
}
